import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import uniqBy from 'lodash/uniqBy'
import { useSelector } from 'react-redux'
import { message, Spin } from 'antd'
import first from 'lodash/first'
import { Form, Field } from 'react-final-form'
import { diff } from 'deep-object-diff'
import arrayMutators from 'final-form-arrays'
import useAction from 'utils/useAction'
import Input from 'components/Input/Input'
import Select from 'components/Select/Select'
import ImagePicker from 'components/ImagePicker'
import FieldWrapper from 'components/FieldWrapper/FieldWrapper'
import Checkbox from 'components/Checkbox/Checkbox'
import SubmitButton from 'components/SubmitButton/SubmitButton'
import CustomRangePicker from './CustomRangePicker'
import { actions as adPackageActions } from 'domains/adPackages'
import { FlexWrapper } from './ShopAdPackage.style'
import {
  WEB_CROP_SIZE,
  MOBILE_CROP_SIZE,
  MOBILE_SEE_ALL_CROP_SIZE,
  BANNER_CROP_SIZE,
  PACKAGE_STATUS_OPTIONS,
  GENDER_OPTIONS,
  MEDIA_TYPE_OPTIONS,
} from 'const/retailers'
import {
  required,
  bodyLength,
  headerLength,
  validBannerLink,
  validDestinationUrl,
  requireBannerField,
  requiredSalesImage,
} from 'helpers/validators'
import { FIELD_NAMES } from 'const/forms'
import {
  getAdPackagesFilters,
  getAdPackagesSorter,
  getCurrentShopId,
  getShopAdPackageById,
} from 'domains/shops/selectors'
import { fetchShopAdPackages } from 'actions/views/shops'
import { PACKAGE_TYPE_OPTIONS } from 'const/shops'
import { DEFAULT_MEDIA_TYPE } from 'const/adPackage'
import { getLocalizedShops } from 'domains/shops/api'
import PackageRegions from "../../PackageRegions"

const ShopAdPackage = ({ closeModal, id, createMode }) => {
  const [loaded, setLoaded] = useState(false)
  const [currentShopLocalizedShops, setCurrentShopLocalizedShops] = useState([])
  const updateAdPackage = useAction(adPackageActions.updateAdPackageRequest)
  const createAdPackage = useAction(adPackageActions.createAdPackageRequest)
  const resetRegionFunc = useAction(adPackageActions.resetRegion)
  const fetchAdPackages = useAction(fetchShopAdPackages)

  const filters = useSelector(getAdPackagesFilters)
  const sorter = useSelector(getAdPackagesSorter)
  const shopId = useSelector(getCurrentShopId)

  const adPackage = useSelector(getShopAdPackageById)(id).attributes
  const adPackageAsset = useSelector(getShopAdPackageById)(id).ad_package_asset
  const formattedAdPackage = {
    ...adPackage,
    [FIELD_NAMES.mediaType]:
      adPackage?.[FIELD_NAMES.mediaType] || DEFAULT_MEDIA_TYPE,
    [FIELD_NAMES.retailerId]: adPackage?.[FIELD_NAMES.retailer]?.value,
  }

  const initialValues = {
    ...formattedAdPackage,
    id,
    [FIELD_NAMES.headerText]:
      adPackageAsset?.[FIELD_NAMES.attributes]?.[FIELD_NAMES.headerText] || '',
    [FIELD_NAMES.bodyText]:
      adPackageAsset?.[FIELD_NAMES.attributes]?.[FIELD_NAMES.bodyText] || '',
    [FIELD_NAMES.bannerImage]:
      adPackageAsset?.[FIELD_NAMES.attributes]?.[FIELD_NAMES.bannerImage] || '',
    [FIELD_NAMES.bannerText]:
      adPackageAsset?.[FIELD_NAMES.attributes]?.[FIELD_NAMES.bannerText] || '',
    [FIELD_NAMES.bannerLink]:
      adPackageAsset?.[FIELD_NAMES.attributes]?.[FIELD_NAMES.bannerLink] || '',
  }

  const retailerSelectOptions = useMemo(
    () =>
      uniqBy(
        currentShopLocalizedShops,
        (shop) => shop.attributes.retailer_id
      ).map((shop) => ({
        value: shop.attributes.retailer_id,
        label: shop.attributes.domain,
      })),
    [currentShopLocalizedShops]
  )

  const onSubmit = async (values) => {
    const retailerId = values.retailer_id

    delete values.retailer_id
    delete values.retailer_name
    delete values.regions_groups
    const changes = diff(formattedAdPackage, values)
    const textChanges = {
      [FIELD_NAMES.headerText]: changes.hasOwnProperty(FIELD_NAMES.headerText)
        ? changes[FIELD_NAMES.headerText] || null
        : undefined,
      [FIELD_NAMES.bodyText]: changes.hasOwnProperty(FIELD_NAMES.bodyText)
        ? changes[FIELD_NAMES.bodyText] || null
        : undefined,
      [FIELD_NAMES.bannerImage]: changes.hasOwnProperty(FIELD_NAMES.bannerImage)
        ? changes[FIELD_NAMES.bannerImage] || null
        : undefined,
      [FIELD_NAMES.bannerText]: changes.hasOwnProperty(FIELD_NAMES.bannerText)
        ? changes[FIELD_NAMES.bannerText] || null
        : undefined,
      [FIELD_NAMES.bannerLink]: changes.hasOwnProperty(FIELD_NAMES.bannerLink)
        ? changes[FIELD_NAMES.bannerLink] || null
        : undefined,
      [FIELD_NAMES.destinationUrl]: changes.hasOwnProperty(FIELD_NAMES.destinationUrl)
        ? changes[FIELD_NAMES.destinationUrl] || null
        : undefined,
    }

    const hasNewValidStartDate = changes.hasOwnProperty(FIELD_NAMES.startDate)
      ? values[FIELD_NAMES.startDate] &&
        !moment(changes[FIELD_NAMES.startDate]).isBefore()
      : !!values[FIELD_NAMES.startDate]

    const formattedChanges = {
      ...changes,
      shopId,
      [FIELD_NAMES.mediaType]: values[FIELD_NAMES.mediaType],
      [FIELD_NAMES.startDate]: hasNewValidStartDate
        ? changes[FIELD_NAMES.startDate]
        : moment().toISOString(),
      [FIELD_NAMES.retailer]: retailerId
        ? {
            value: retailerId,
          }
        : undefined,
    }

    const errors = !id
      ? await createAdPackage(formattedChanges)
      : await updateAdPackage({
          ...formattedChanges,
          ...textChanges,
          ad_package_asset_id: adPackageAsset?.id,
          [FIELD_NAMES.regions]: values[FIELD_NAMES.regions],
          id: id.toString(),
        })
    if (errors) {
      message.error('Error occured')
    } else {
      message.success('Saved')

      if (!id) {
        fetchAdPackages({
          filters,
          sorter,
        })
      }

      closeModal()
    }
  }

  useEffect(() => {
    const loadShopRetailers = async () => {
      const { data } =
        (await getLocalizedShops({ search: shopId, perPage: 150 })) || {}

      if (data?.included) {
        setCurrentShopLocalizedShops(data.included)
      }
      setLoaded(true)
    }

    loadShopRetailers()
  }, [])

  useEffect(() => {
    return () => resetRegionFunc()
  }, [])

  return (
    <Form
      destroyOnUnregister
      onSubmit={onSubmit}
      mutators={{
        ...arrayMutators,
      }}
      initialValues={initialValues}
      render={({ handleSubmit, submitting, pristine, values }) => (
        <Spin spinning={submitting || !loaded} size="large">
          <form onSubmit={handleSubmit}>
            {id && (
              <Field
                name="id"
                label="Ad Package ID"
                disabled
                component={Input}
              />
            )}
            <Field
              required
              name={FIELD_NAMES.name}
              validate={required}
              label="Ad Package Name"
              placeholder="Name"
              component={Input}
            />
            <Field
              required
              label="Retailer"
              options={retailerSelectOptions}
              name={FIELD_NAMES.retailerId}
              validate={required}
              component={Select}
              value={values[FIELD_NAMES.retailer]?.value}
            />
            <Field
              label="Status"
              options={PACKAGE_STATUS_OPTIONS}
              defaultValue="not_opened"
              name={FIELD_NAMES.status}
              component={Select}
            />
            <PackageRegions/>
            <CustomRangePicker createMode={createMode} />

            <Field
              label="Gender"
              options={GENDER_OPTIONS}
              defaultValue="all_gender"
              name={FIELD_NAMES.gender}
              component={Select}
            />
            <Field
              validate={headerLength}
              name={FIELD_NAMES.headerText}
              placeholder="Header Text"
              label="Header"
              component={Input}
            />
            <Field
              validate={bodyLength}
              name={FIELD_NAMES.bodyText}
              label="Body"
              placeholder="Body Text"
              component={Input}
            />
            <Field
              name={FIELD_NAMES.mediaType}
              label="Media type (Badge)"
              defaultValue={first(MEDIA_TYPE_OPTIONS).value}
              options={MEDIA_TYPE_OPTIONS}
              component={Select}
            />
            <Field
              name={FIELD_NAMES.packageType}
              label="Package type"
              defaultValue={first(PACKAGE_TYPE_OPTIONS).value}
              options={PACKAGE_TYPE_OPTIONS}
              component={Select}
            />
            <Field
              name={FIELD_NAMES.textOnly}
              label="Text Ad"
              component={Checkbox}
            />
            <Field
              name={FIELD_NAMES.destinationUrl}
              label="Destination URL"
              placeholder="https://example.com"
              validate={validDestinationUrl}
              component={Input}
            />
            {!values?.[FIELD_NAMES.textOnly] && (
              <>
                <FieldWrapper label="Sales web" required>
                  <FlexWrapper>
                    <Field
                      name={FIELD_NAMES.salesWeb1}
                      hasCropper
                      validate={requiredSalesImage(FIELD_NAMES.salesWeb2)}
                      cropSize={WEB_CROP_SIZE}
                      component={ImagePicker}
                    />
                    <Field
                      hasCropper
                      validate={requiredSalesImage(FIELD_NAMES.salesWeb1)}
                      cropSize={WEB_CROP_SIZE}
                      name={FIELD_NAMES.salesWeb2}
                      component={ImagePicker}
                    />
                  </FlexWrapper>
                </FieldWrapper>
                <FieldWrapper label="Sales mobile" required>
                  <FlexWrapper>
                    <Field
                      hasCropper
                      validate={requiredSalesImage(FIELD_NAMES.salesMobile2)}
                      cropSize={MOBILE_CROP_SIZE}
                      name={FIELD_NAMES.salesMobile1}
                      component={ImagePicker}
                    />
                    <Field
                      hasCropper
                      validate={requiredSalesImage(FIELD_NAMES.salesMobile1)}
                      cropSize={MOBILE_CROP_SIZE}
                      name={FIELD_NAMES.salesMobile2}
                      component={ImagePicker}
                    />
                  </FlexWrapper>
                </FieldWrapper>
                <FieldWrapper label="Sales mobile see all" required>
                  <FlexWrapper>
                    <Field
                      hasCropper
                      validate={requiredSalesImage(
                        FIELD_NAMES.salesMobileSeeAll2
                      )}
                      cropSize={MOBILE_SEE_ALL_CROP_SIZE}
                      name={FIELD_NAMES.salesMobileSeeAll1}
                      component={ImagePicker}
                    />
                    <Field
                      hasCropper
                      validate={requiredSalesImage(
                        FIELD_NAMES.salesMobileSeeAll1
                      )}
                      cropSize={MOBILE_SEE_ALL_CROP_SIZE}
                      name={FIELD_NAMES.salesMobileSeeAll2}
                      component={ImagePicker}
                    />
                  </FlexWrapper>
                </FieldWrapper>
                <FieldWrapper label="Banner" required>
                  <FlexWrapper>
                    <Field
                      hasCropper
                      validate={requireBannerField}
                      cropSize={BANNER_CROP_SIZE}
                      name={FIELD_NAMES.bannerImage}
                      component={ImagePicker}
                    />
                    <FlexWrapper column>
                      <Field
                        name={FIELD_NAMES.bannerText}
                        placeholder="Banner Text"
                        component={Input}
                      />
                      <Field
                        validate={validBannerLink}
                        name={FIELD_NAMES.bannerLink}
                        placeholder="Link"
                        component={Input}
                      />
                    </FlexWrapper>
                  </FlexWrapper>
                </FieldWrapper>
              </>
            )}
            <SubmitButton
              handleSubmit={handleSubmit}
              pristine={pristine}
              text="Submit"
            />
          </form>
        </Spin>
      )}
    />
  )
}

export default ShopAdPackage
